/**
 * Hex color helpers plus WCAG-style contrast.
 */
export function parseCssHex(hex: string): [number, number, number] | null {
  const match = hex.trim().match(/^#?([0-9a-f]{3}|[0-9a-f]{6})$/i);
  if (!match) return null;
  let value = match[1];
  if (value.length === 3) {
    value = value.split('').map((digit) => digit + digit).join('');
  }
  const n = parseInt(value, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

export function formatCssHex(r: number, g: number, b: number): string {
  const toHex = (channel: number) => Math.max(0, Math.min(255, Math.round(channel)))
    .toString(16)
    .padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

/** Multiply RGB by (1 - amount). Amount is clamped to 0–1. Unknown colors are returned unchanged. */
export function darkenHex(hex: string, amount: number): string {
  const rgb = parseCssHex(hex);
  if (!rgb) return hex;
  const t = Math.max(0, Math.min(1, amount));
  return formatCssHex(rgb[0] * (1 - t), rgb[1] * (1 - t), rgb[2] * (1 - t));
}

export function darkenPalette(colors: string[], amount: number): string[] {
  return colors.map((color) => darkenHex(color, amount));
}

export function darkenRgbNum(color: number, amount: number): number {
  const t = 1 - Math.max(0, Math.min(1, amount));
  const r = Math.round(((color >> 16) & 255) * t);
  const g = Math.round(((color >> 8) & 255) * t);
  const b = Math.round((color & 255) * t);
  return (r << 16) | (g << 8) | b;
}

/** Mix RGB toward white by `amount` (clamped to 0–1). */
export function lightenRgbNum(color: number, amount: number): number {
  const t = Math.max(0, Math.min(1, amount));
  const mix = (channel: number) => Math.round(channel + (255 - channel) * t);
  return (mix((color >> 16) & 255) << 16) | (mix((color >> 8) & 255) << 8) | mix(color & 255);
}

const rgbNumCache = new Map<string, number>();
/**
 * A CSS hex color as an `0xRRGGBB` number (black when it isn't a color). Cached:
 * boards look up the same few palette colors once per cell.
 */
export function hexToRgbNum(hex: string): number {
  let value = rgbNumCache.get(hex);
  if (value === undefined) {
    const rgb = parseCssHex(hex);
    value = rgb ? (rgb[0] << 16) | (rgb[1] << 8) | rgb[2] : 0;
    rgbNumCache.set(hex, value);
  }
  return value;
}

export function contrastColor(hex: string): 0xffffff | 0x000000 {
  const rgb = parseCssHex(hex);
  if (!rgb) return 0x000000;
  const [r, g, b] = rgb;
  const lum = [r, g, b].map(c => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  });
  const bgL = 0.2126 * lum[0] + 0.7152 * lum[1] + 0.0722 * lum[2];
  return (1.05) / (bgL + 0.05) > (bgL + 0.05) / 0.05 ? 0xffffff : 0x000000;
}
